'use client';

// UI components
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Vendors
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  FileText,
  LayoutDashboard,
  LifeBuoy,
  ListTodo,
  Mail,
  Receipt,
  Settings,
  Users,
} from 'lucide-react';
import AvatarCont from './avatar';
import { ExtendedUser } from '@/types/next-auth';

const LOCALES = ['en', 'sr', 'si'] as const;

export function AccountMenu({
  t,
  user,
}: {
  t: (key: string) => string;
  user: ExtendedUser;
}) {
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as (typeof LOCALES)[number];

  return (
    <>
      <DropdownMenuLabel>
        <AvatarCont user={user} />
      </DropdownMenuLabel>

      <DropdownMenuSeparator />

      {user.role === 'ACCOUNTANT' ? (
        <div>
          <Link href={`/${locale}/accountant`}>
            <DropdownMenuItem className="cursor-pointer">
              <LayoutDashboard className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('accountantDashboard')}
              </span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />

          <Link href={`/${locale}/settings`}>
            <DropdownMenuItem className="cursor-pointer">
              <Settings className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('accountSettings')}
              </span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />
        </div>
      ) : user.role === 'ADMIN' ? (
        <div>
          <Link href={`/${locale}/admin`}>
            <DropdownMenuItem className="cursor-pointer">
              <LayoutDashboard className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('adminDashboard') || 'Admin Dashboard'}
              </span>
            </DropdownMenuItem>
          </Link>

          <Link href={`/${locale}/admin/emails`}>
            <DropdownMenuItem className="cursor-pointer">
              <Mail className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('emailLogs')}</span>
            </DropdownMenuItem>
          </Link>

          <Link href={`/${locale}/admin/tickets`}>
            <DropdownMenuItem className="cursor-pointer">
              <LifeBuoy className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('supportTickets')}</span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />

          <Link href={`/${locale}/settings`}>
            <DropdownMenuItem className="cursor-pointer">
              <Settings className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('accountSettings')}
              </span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />
        </div>
      ) : (
        <div>
          <Link href={`/${locale}/dashboard`}>
            <DropdownMenuItem className="cursor-pointer">
              <LayoutDashboard className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('dashboard')}</span>
            </DropdownMenuItem>
          </Link>

          <Link href={`/${locale}/invoices`}>
            <DropdownMenuItem className="cursor-pointer">
              <FileText className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('invoices')}</span>
            </DropdownMenuItem>
          </Link>

          <Link href={`/${locale}/clients`}>
            <DropdownMenuItem className="cursor-pointer">
              <Users className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('clients')}</span>
            </DropdownMenuItem>
          </Link>

          {user.enableProducts && (
            <Link href={`/${locale}/products`}>
              <DropdownMenuItem className="cursor-pointer">
                <ListTodo className="w-4 h-4" color="#787B80" />
                <span className="text-sm font-medium">{t('products')}</span>
              </DropdownMenuItem>
            </Link>
          )}

          {user.enableExpenses && (
            <Link href={`/${locale}/expenses`}>
              <DropdownMenuItem className="cursor-pointer">
                <Receipt className="w-4 h-4" color="#787B80" />
                <span className="text-sm font-medium">{t('expenses')}</span>
              </DropdownMenuItem>
            </Link>
          )}

          <Link href={`/${locale}/support`}>
            <DropdownMenuItem className="cursor-pointer">
              <LifeBuoy className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('support')}</span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />

          <Link href={`/${locale}/settings`}>
            <DropdownMenuItem className="cursor-pointer">
              <Settings className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('accountSettings')}
              </span>
            </DropdownMenuItem>
          </Link>

          <DropdownMenuSeparator />
        </div>
      )}
    </>
  );
}
